import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
	cancelSchedule,
	createSchedule,
	editSchedule,
	type ScheduleCreateInput,
	type ScheduleEditInput,
} from '@/api/adapters/patients';
import { toast } from '@/app/store/toast.store';
import { ApiError } from '@/api/error';

/**
 * Mutation hook: create a new schedule appointment.
 * Invalidates the intervention timeline + home dashboard on success.
 */
export function useCreateSchedule() {
	const queryClient = useQueryClient();
	const { t } = useTranslation('medical');

	return useMutation({
		mutationFn: (input: ScheduleCreateInput) => createSchedule(input),
		onSuccess: () => {
			toast.success(t('patients.schedule.addSuccess'));
			queryClient.invalidateQueries({ queryKey: ['intervention'] });
			queryClient.invalidateQueries({ queryKey: ['home-dashboard'] });
		},
		onError: (err) => {
			if (err instanceof ApiError) {
				toast.error(err.message);
			} else {
				toast.error(t('common:error.generic'));
			}
		},
	});
}

/**
 * Mutation hook: edit a future schedule appointment.
 * Invalidates the intervention timeline + home dashboard on success.
 */
export function useEditSchedule() {
	const queryClient = useQueryClient();
	const { t } = useTranslation('medical');

	return useMutation({
		mutationFn: ({
			id,
			input,
		}: {
			id: string | number;
			input: ScheduleEditInput;
		}) => editSchedule(id, input),
		onSuccess: () => {
			toast.success(t('patients.schedule.editSuccess'));
			queryClient.invalidateQueries({ queryKey: ['intervention'] });
			queryClient.invalidateQueries({ queryKey: ['home-dashboard'] });
		},
		onError: (err) => {
			if (err instanceof ApiError) {
				toast.error(err.message);
			} else {
				toast.error(t('common:error.generic'));
			}
		},
	});
}

/**
 * Mutation hook: cancel a future schedule (not recoverable).
 * Invalidates the intervention timeline + home dashboard on success.
 */
export function useCancelSchedule() {
	const queryClient = useQueryClient();
	const { t } = useTranslation('medical');

	return useMutation({
		mutationFn: (id: string | number) => cancelSchedule(id),
		onSuccess: () => {
			toast.success(t('patients.schedule.cancelSuccess'));
			queryClient.invalidateQueries({ queryKey: ['intervention'] });
			queryClient.invalidateQueries({ queryKey: ['home-dashboard'] });
		},
		onError: (err) => {
			if (err instanceof ApiError) {
				toast.error(err.message);
			} else {
				toast.error(t('common:error.generic'));
			}
		},
	});
}
